import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import PropTypes from 'prop-types';
import { FaGlobe, FaLock, FaMapMarkedAlt, FaTimes, FaSave } from 'react-icons/fa';
import Select from 'react-select';
import { regionAPI, districtAPI } from '../../../../utils/api';
import '../../../../styles/institutional-form.css';

function RegionCreate({ region = null, onClose, onSubmit }) {
  const isEdit = !!region;

  const [code, setCode]       = useState('');
  const [name, setName]       = useState('');
  const [districtIds, setDistrictIds] = useState([]);
  const [districtOpts, setDistrictOpts] = useState([]);
  const [errors, setErrors]   = useState({});
  const [busy, setBusy]       = useState(false);
  const [codeLoading, setCodeLoading] = useState(!isEdit);

  // Load districts and (if editing) prefill form
  useEffect(() => {
    districtAPI.getAll()
      .then(r => setDistrictOpts((r.data || []).map(d => ({ value: Number(d.id), label: d.name }))))
      .catch(() => setDistrictOpts([]));

    if (isEdit) {
      setCode(region.code || region.region_id || '');
      setName(region.name || '');
      const ids = Array.isArray(region.district_ids)
        ? region.district_ids.map(Number)
        : String(region.district_ids || '').split('|').filter(Boolean).map(Number);
      setDistrictIds(ids);
    } else {
      setCodeLoading(true);
      regionAPI.nextCode()
        .then(r => setCode(r.data.code))
        .catch(() => setCode('RGN-???'))
        .finally(() => setCodeLoading(false));
    }
  }, [isEdit, region]);

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = 'Region name is required';
    if (!districtIds.length) e.districtIds = 'Select at least one district';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async ev => {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const payload = { name: name.trim(), code, district_ids: districtIds };
      if (isEdit) await regionAPI.update(region.id, payload);
      else        await regionAPI.create(payload);
      Swal.fire('Success', isEdit ? 'Region updated.' : 'Region created.', 'success');
      onSubmit?.();
      onClose?.();
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.error || err.message || 'Failed', 'error');
    } finally { setBusy(false); }
  };

  return (
    <form className="ifrm" onSubmit={handleSubmit}>

      {/* Auto-generated code */}
      <div className="ifrm-section">
        <div className="ifrm-section-title"><FaLock style={{color:'#10ac84'}} /> System Identifier</div>
        <div className="ifrm-field">
          <label className="ifrm-label">
            Region Code <span className="ifrm-badge-auto">Auto-generated</span>
          </label>
          {codeLoading
            ? <p className="ifrm-code-loading">Generating code…</p>
            : (
              <div className="ifrm-locked-wrap">
                <FaLock className="ifrm-lock-icon" />
                <input className="ifrm-input" value={code} disabled />
              </div>
            )
          }
        </div>
      </div>

      {/* Basic info */}
      <div className="ifrm-section">
        <div className="ifrm-section-title"><FaGlobe style={{color:'#0f6fad'}} /> Region Details</div>

        <div className="ifrm-field">
          <label className="ifrm-label">
            Region Name <span className="ifrm-required">*</span>
          </label>
          <input
            className={`ifrm-input${errors.name ? ' error' : ''}`}
            placeholder="e.g. Sindh Central"
            value={name}
            onChange={e => { setName(e.target.value); setErrors(p => ({...p, name:''})); }}
            disabled={busy}
          />
          {errors.name && <p className="ifrm-error">{errors.name}</p>}
        </div>

        <div className="ifrm-field">
          <label className="ifrm-label">
            <FaMapMarkedAlt style={{color:'#64748b'}} /> Linked Districts <span className="ifrm-required">*</span>
          </label>
          <Select
            isMulti
            classNamePrefix="ifrm-rselect"
            className="ifrm-rselect"
            options={districtOpts}
            value={districtOpts.filter(o => districtIds.includes(o.value))}
            onChange={opts => { setDistrictIds(opts.map(o => o.value)); setErrors(p => ({...p, districtIds:''})); }}
            placeholder="Select districts…"
            isDisabled={busy}
          />
          {errors.districtIds && <p className="ifrm-error">{errors.districtIds}</p>}
        </div>
      </div>

      <div className="ifrm-actions">
        <button type="button" className="ifrm-btn ifrm-btn-cancel" onClick={onClose} disabled={busy}>
          <FaTimes /> Cancel
        </button>
        <button type="submit" className="ifrm-btn ifrm-btn-submit" disabled={busy || codeLoading}>
          {busy ? <><span className="ifrm-spinner" /> Saving…</> : <><FaSave /> {isEdit ? 'Update Region' : 'Create Region'}</>}
        </button>
      </div>
    </form>
  );
}

RegionCreate.propTypes = { region: PropTypes.object, onClose: PropTypes.func, onSubmit: PropTypes.func };
export default RegionCreate;
